import { Gift } from 'lucide-react';
import { useEffect, useState } from 'react';

interface GiftCard {
  id: string;
  code: string;
  originalValueMinor: number;
  remainingValueMinor: number;
  currency: string;
  status: string;
  recipientEmail: string | null;
  recipientName: string | null;
  expiresAt: string;
  issuedAt: string;
}

function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

/**
 * Staff-facing gift card audit + issue surface. Issuance form + table of
 * existing cards with void button.
 */
export function GiftCardsSettings() {
  const [cards, setCards] = useState<GiftCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [amountMajor, setAmountMajor] = useState('25.00');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    const res = await fetch('/api/gift-cards', { credentials: 'include' });
    const data = (await res.json()) as { cards: GiftCard[] };
    setCards(data.cards ?? []);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
  }, []);

  async function issue() {
    const amountMinor = Math.round(Number.parseFloat(amountMajor) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      setMessage('Enter a positive amount.');
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/gift-cards', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amountMinor,
          recipientEmail: recipientEmail || null,
          recipientName: recipientName || null,
        }),
      });
      if (res.ok) {
        setMessage('Gift card issued.');
        setAmountMajor('25.00');
        setRecipientEmail('');
        setRecipientName('');
        await refresh();
      } else {
        setMessage('Issue failed.');
      }
    } catch {
      setMessage('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function voidCard(id: string) {
    if (!confirm('Void this gift card? This cannot be undone.')) return;
    const res = await fetch(`/api/gift-cards/${id}/void`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) refresh();
  }

  if (loading) return <div className="py-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8 py-6">
      <header className="flex items-center gap-2">
        <Gift className="h-6 w-6" />
        <h1 className="text-2xl font-semibold">Gift cards</h1>
      </header>

      {message && <div className="rounded-md border bg-muted px-3 py-2 text-sm">{message}</div>}

      <section className="space-y-3 rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold">Issue a card</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void issue();
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Amount</span>
              <input
                value={amountMajor}
                onChange={(e) => setAmountMajor(e.target.value)}
                className="rounded-md border bg-background px-2 py-1"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Recipient email</span>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="rounded-md border bg-background px-2 py-1"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Recipient name</span>
              <input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="rounded-md border bg-background px-2 py-1"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {saving ? 'Issuing…' : 'Issue card'}
          </button>
        </form>
      </section>

      <section className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="min-w-[640px] w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Original</th>
                <th className="px-4 py-2">Remaining</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Recipient</th>
                <th className="px-4 py-2">Issued</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {cards.map((card) => (
                <tr key={card.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{card.code}</td>
                  <td className="px-4 py-2">
                    {formatMoney(card.originalValueMinor, card.currency)}
                  </td>
                  <td className="px-4 py-2">
                    {formatMoney(card.remainingValueMinor, card.currency)}
                  </td>
                  <td className="px-4 py-2">{card.status}</td>
                  <td className="px-4 py-2">{card.recipientName ?? card.recipientEmail ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(card.issuedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    {card.status === 'active' && (
                      <button
                        type="button"
                        onClick={() => voidCard(card.id)}
                        className="text-xs text-red-600 underline"
                      >
                        Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {cards.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    No gift cards issued yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
